import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Boxes,
  AlertTriangle,
  Receipt,
  ClipboardCheck,
  ArrowDownToLine,
  ArrowUpFromLine,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const PIE_COLORS = ["#0EA5E9", "#10B981", "#F59E0B", "#F43F5E", "#A78BFA", "#64748B"];
const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function StatCard({ icon: Icon, label, value, accent, testid, sub }) {
  return (
    <Card className="bg-card border-border h-full" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-mono-sku">{label}</p>
            <p className="text-3xl font-extrabold mt-2">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!stats)
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
      </div>
    );

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Operations Overview</h1>
        <p className="text-muted-foreground mt-1">Live snapshot of stock, expenses and gate passes.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Boxes}
          label="Total Items"
          value={stats.total_items}
          accent="bg-sky-500/15 text-sky-400"
          testid="stat-total-items"
          sub={`${stats.inward_count} inward · ${stats.outward_count} outward entries`}
        />
        <StatCard
          icon={AlertTriangle}
          label="Low Stock"
          value={stats.low_stock_count}
          accent="bg-amber-500/15 text-amber-400"
          testid="stat-low-stock"
          sub="items at / below minimum"
        />
        <StatCard
          icon={Receipt}
          label="Expense (Month)"
          value={fmt(stats.month_expense_total)}
          accent="bg-rose-500/15 text-rose-400"
          testid="stat-month-expense"
          sub={`Total: ${fmt(stats.total_expense)}`}
        />
        <StatCard
          icon={ClipboardCheck}
          label="Pending Gate Pass"
          value={stats.pending_gatepasses}
          accent="bg-emerald-500/15 text-emerald-400"
          testid="stat-pending-gatepass"
          sub={`${stats.total_gatepasses} total passes`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-sky-400" /> Expense Trend (6 months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.expense_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `₹${v / 1000}k`} />
                <Tooltip
                  contentStyle={{ background: "#111827", border: "1px solid #1e293b", borderRadius: 8 }}
                  formatter={(v) => fmt(v)}
                />
                <Bar dataKey="amount" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg">Expense by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.expense_by_category.length === 0 ? (
              <p className="text-sm text-muted-foreground py-16 text-center">No expenses this month.</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={stats.expense_by_category}
                    dataKey="amount"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    innerRadius={45}
                    paddingAngle={2}
                  >
                    {stats.expense_by_category.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid #1e293b", borderRadius: 8 }}
                    formatter={(v) => fmt(v)}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="space-y-1 mt-2">
              {stats.expense_by_category.map((c, i) => (
                <div key={c.category} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    {c.category}
                  </span>
                  <span className="font-mono-sku">{fmt(c.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">Stock Movement (This Month)</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-4">
            <div className="flex-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-5">
              <ArrowDownToLine className="h-6 w-6 text-emerald-400 mb-2" />
              <p className="text-3xl font-extrabold text-emerald-400">{stats.inward_month}</p>
              <p className="text-sm text-muted-foreground">Inward entries</p>
            </div>
            <div className="flex-1 rounded-lg bg-rose-500/10 border border-rose-500/20 p-5">
              <ArrowUpFromLine className="h-6 w-6 text-rose-400 mb-2" />
              <p className="text-3xl font-extrabold text-rose-400">{stats.outward_month}</p>
              <p className="text-sm text-muted-foreground">Outward entries</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg">Recent Movements</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_movements.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No movements yet.</p>
            ) : (
              <div className="space-y-2">
                {stats.recent_movements.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between py-2 border-b border-border/60 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      {m.type === "inward" ? (
                        <ArrowDownToLine className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <ArrowUpFromLine className="h-4 w-4 text-rose-400" />
                      )}
                      <div>
                        <p className="text-sm font-medium">{m.item_name}</p>
                        <p className="text-xs text-muted-foreground">{m.date} · {m.party || "—"}</p>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={m.type === "inward" ? "text-emerald-400 border-emerald-500/30" : "text-rose-400 border-rose-500/30"}
                    >
                      {m.type === "inward" ? "+" : "-"}
                      {m.quantity} {m.unit}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
